import React, { useState } from "react";
import { posAPI } from "../../services/api";
import { useI18n } from "../../i18n";
import { formatClock } from "../../utils/dates";
import { formatMoney } from "../utils/money";
import { sanitizeAmount } from "../utils/amountEntry";
import Sheet from "./Sheet";

const input =
  "w-full h-12 px-3 rounded-xl bg-slate-800 border border-slate-700 text-white text-end text-xl font-bold placeholder-slate-500 focus:outline-none focus:border-primary-400";

/**
 * The drawer during the day and at its end.
 *
 * Anyone on the till can put change in and close the drawer. Only someone
 * allowed to manage cash (`reveal`) sees what it should hold, can take money
 * out, and can take the day's cash home at closing. Closing is a blind count
 * — the figure it should be comes back only afterwards, and only to them.
 */
const REASONS_IN = ["float", "owner_investment", "from_safe"];
const REASONS_OUT = ["expense", "supplier", "to_safe", "personal_use", "correction"];
const EXPENSE_CATEGORIES = [
  "rent",
  "salary",
  "utilities",
  "supplies",
  "transport",
  "marketing",
  "equipment",
  "fees",
  "other",
];

const DrawerSheet = ({ open, onClose, drawer, reveal, currency, onChanged, onPaySupplier }) => {
  const { t } = useI18n();
  const money = (value) => formatMoney(value, currency);
  const [step, setStep] = useState("menu");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState(null);
  const [category, setCategory] = useState("");
  const [counted, setCounted] = useState("");
  const [takenOut, setTakenOut] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setStep("menu");
    setAmount("");
    setNote("");
    setReason(null);
    setCategory("");
    setCounted("");
    setTakenOut("");
    setResult(null);
    setError("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || t("pos.drawer.failed"));
    } finally {
      setBusy(false);
    }
  };

  const move = (type) =>
    run(async () => {
      await posAPI.moveCash(type, parseFloat(amount), note || undefined, {
        reason,
        expense_category: reason === "expense" ? category : undefined,
      });
      reset();
    });

  // Someone who may not manage cash can only put change in: nothing to choose
  const choices =
    step === "cash_in"
      ? reveal
        ? REASONS_IN
        : ["float"]
      : REASONS_OUT.filter((value) => value !== "supplier" || onPaySupplier);
  const pick = (value) => {
    if (value === "supplier") {
      // Paying a supplier has a screen of its own, which also lowers what is owed
      close();
      onPaySupplier?.();
      return;
    }
    setReason(value);
  };
  const startStep = (next) => {
    setStep(next);
    setReason(next === "cash_in" && !reveal ? "float" : null);
  };
  const needsWhat = reason === "expense";
  const ready =
    parseFloat(amount) > 0 &&
    reason &&
    (!needsWhat || (category && note.trim()));

  const closeDay = () =>
    run(async () => {
      const { data } = await posAPI.closeDrawer({
        counted: parseFloat(counted || "0"),
        taken_out: reveal && takenOut !== "" ? parseFloat(takenOut) : 0,
        note: note || undefined,
      });
      setResult(data);
      setStep("closed");
    });

  const breakdown = drawer.breakdown;
  const button =
    "w-full h-12 rounded-xl bg-slate-800 border border-slate-700 text-sm font-semibold text-slate-100 hover:bg-slate-700";
  const primary =
    "w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-white text-lg font-bold disabled:opacity-40";

  return (
    <Sheet
      open={open}
      onClose={close}
      title={t("pos.drawer.title")}
      subtitle={
        drawer.session
          ? t("pos.drawer.openSince", {
              time: formatClock(drawer.session.opened_at),
              name: drawer.session.opened_by_name || "—",
            })
          : undefined
      }
    >
      <div className="p-4 space-y-4">
        {step === "menu" && (
          <>
            {reveal && drawer.expected !== undefined && (
              <div className="rounded-2xl bg-slate-800/60 border border-slate-700 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {t("pos.drawer.shouldHold")}
                </p>
                <p className="mt-1 text-3xl font-extrabold tabular-nums text-emerald-300" dir="ltr">
                  {money(drawer.expected)}
                </p>
                {breakdown && (
                  <ul className="mt-3 space-y-1 text-sm text-slate-300">
                    <li className="flex justify-between gap-3">
                      <span>{t("pos.drawer.lines.opening")}</span>
                      <span className="tabular-nums">{money(breakdown.opening_count)}</span>
                    </li>
                    <li className="flex justify-between gap-3">
                      <span>{t("pos.drawer.lines.sales", { count: breakdown.sales })}</span>
                      <span className="tabular-nums">+{money(breakdown.cash_sales)}</span>
                    </li>
                    {breakdown.movements.map((movement) => (
                      <li key={movement.id} className="flex justify-between gap-3">
                        <span className="truncate">
                          {movement.reason
                            ? t(`pos.drawer.reasons.${movement.reason}`)
                            : t(`pos.drawer.movements.${movement.type}`)}
                          {movement.note && ` · ${movement.note}`}
                        </span>
                        <span className="tabular-nums shrink-0">
                          {["cash_in", "tab_payment"].includes(movement.type) ? "+" : "−"}
                          {money(movement.amount)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <button type="button" className={button} onClick={() => startStep("cash_in")}>
              ＋ {t("pos.drawer.addChange")}
            </button>
            {reveal && (
              <button type="button" className={button} onClick={() => startStep("withdrawal")}>
                − {t("pos.drawer.takeOut")}
              </button>
            )}
            <button
              type="button"
              className="w-full h-12 rounded-xl bg-amber-500/15 border border-amber-500/40 text-sm font-bold text-amber-200"
              onClick={() => setStep("count")}
            >
              {t("pos.drawer.closeDay")}
            </button>
          </>
        )}

        {(step === "cash_in" || step === "withdrawal") && (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              {step === "cash_in" ? t("pos.drawer.addChange") : t("pos.drawer.takeOut")}
            </p>

            {choices.length > 1 && (
              <div className="space-y-2" role="radiogroup" aria-label={t("pos.drawer.reasonTitle")}>
                <p className="text-sm text-slate-300">{t("pos.drawer.reasonTitle")}</p>
                {choices.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={reason === value}
                    onClick={() => pick(value)}
                    className={`w-full text-start rounded-xl border px-3 py-2.5 ${
                      reason === value
                        ? "border-emerald-400 bg-emerald-500/15"
                        : "border-slate-700 bg-slate-800 hover:bg-slate-700"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-slate-100">
                      {t(`pos.drawer.reasons.${value}`)}
                    </span>
                    <span className="block text-xs text-slate-400">
                      {t(`pos.drawer.reasonHints.${value}`)}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {reason && (
              <>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {t("pos.drawer.amount")} ({currency})
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    dir="ltr"
                    autoFocus
                    value={amount}
                    onChange={(e) => setAmount(sanitizeAmount(e.target.value))}
                    className={`mt-1 ${input}`}
                  />
                </label>

                {needsWhat && (
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {t("pos.drawer.expenseKind")}
                    </span>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="mt-1 w-full h-11 px-3 rounded-xl bg-slate-800 border border-slate-700 text-white"
                    >
                      <option value="">—</option>
                      {EXPENSE_CATEGORIES.map((value) => (
                        <option key={value} value={value}>
                          {t(`finance.expenses.categories.${value}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {needsWhat ? t("pos.drawer.expenseWhat") : t("pos.drawer.why")}
                  </span>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="mt-1 w-full h-11 px-3 rounded-xl bg-slate-800 border border-slate-700 text-white"
                  />
                </label>
              </>
            )}

            <button
              type="button"
              className={primary}
              disabled={busy || !ready}
              onClick={() => move(step)}
            >
              {busy ? t("common.saving") : t("pos.drawer.record")}
            </button>
            <button type="button" className={button} onClick={reset}>
              {t("common.cancel")}
            </button>
          </>
        )}

        {step === "count" && (
          <>
            <p className="text-sm text-slate-300">{t("pos.drawer.countHint")}</p>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t("pos.drawer.counted", { currency })}
              </span>
              <input
                type="text"
                inputMode="decimal"
                dir="ltr"
                autoFocus
                value={counted}
                onChange={(e) => setCounted(sanitizeAmount(e.target.value))}
                className={`mt-1 ${input}`}
              />
            </label>
            {reveal && (
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {t("pos.drawer.takingHome", { currency })}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  dir="ltr"
                  placeholder="0"
                  value={takenOut}
                  onChange={(e) => setTakenOut(sanitizeAmount(e.target.value))}
                  className={`mt-1 ${input}`}
                />
                <span className="mt-1 block text-xs text-slate-400">
                  {t("pos.drawer.takingHomeHint")}
                </span>
                <span className="mt-1 block text-xs text-slate-400">
                  {t("pos.drawer.leftFor", {
                    amount: money(
                      Math.max((parseFloat(counted) || 0) - (parseFloat(takenOut) || 0), 0),
                    ),
                  })}
                </span>
              </label>
            )}
            <button
              type="button"
              className={primary}
              disabled={busy || counted === "" || (parseFloat(takenOut) || 0) > (parseFloat(counted) || 0)}
              onClick={closeDay}
            >
              {busy ? t("common.saving") : t("pos.drawer.closeNow")}
            </button>
            <button type="button" className={button} onClick={reset}>
              {t("common.cancel")}
            </button>
          </>
        )}

        {step === "closed" && result && (
          <div className="space-y-3">
            <p className="text-lg font-bold text-white">✓ {t("pos.drawer.closed")}</p>
            <ul className="space-y-1 text-sm text-slate-300">
              <li className="flex justify-between">
                <span>{t("pos.drawer.result.counted")}</span>
                <span className="tabular-nums">{money(result.counted)}</span>
              </li>
              <li className="flex justify-between">
                <span>{t("pos.drawer.result.takenOut")}</span>
                <span className="tabular-nums">{money(result.taken_out)}</span>
              </li>
              <li className="flex justify-between font-semibold text-white">
                <span>{t("pos.drawer.result.left")}</span>
                <span className="tabular-nums">{money(result.left_in_drawer)}</span>
              </li>
              {result.expected !== undefined && (
                <>
                  <li className="flex justify-between pt-2 border-t border-slate-800">
                    <span>{t("pos.drawer.result.expected")}</span>
                    <span className="tabular-nums">{money(result.expected)}</span>
                  </li>
                  <li
                    className={`flex justify-between font-semibold ${
                      result.difference === 0
                        ? "text-emerald-300"
                        : result.difference < 0
                          ? "text-red-300"
                          : "text-amber-300"
                    }`}
                  >
                    <span>{t("pos.drawer.result.difference")}</span>
                    <span className="tabular-nums" dir="ltr">
                      {result.difference > 0 ? "+" : ""}
                      {money(result.difference)}
                    </span>
                  </li>
                </>
              )}
            </ul>
            <button type="button" className={primary} onClick={close}>
              {t("common.done")}
            </button>
          </div>
        )}

        {error && <p className="text-sm font-semibold text-red-300">{error}</p>}
      </div>
    </Sheet>
  );
};

export default DrawerSheet;
